/*
 * SignInPage.jsx
 *
 * EVCS-branded landing page shown to signed-out users.
 * Split panel: left is a dark green charger dot grid with cursor spotlight
 * interaction (direct DOM manipulation — no React state updates for perf);
 * right is the Clerk sign-in form. Data Pigeon appears only as "Powered by"
 * footer credit (white-label pattern).
 * Imported by: Dashboard.jsx (rendered inside <SignedOut>).
 */

import { SignInButton } from "@clerk/clerk-react";

export default function SignInPage() {
  return (
    <div className="evcs-outer" style={{ display: "flex", minHeight: "100vh" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@300;400;500&family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; }
        body { font-family: 'Inter', sans-serif; }
        @keyframes evcs-fadein { from { opacity:0; transform:translateY(14px); } to { opacity:1; transform:translateY(0); } }
        .evcs-signin-in { animation: evcs-fadein 0.45s ease both 0.1s; }
        @media (max-width: 768px) {
          .evcs-outer { flex-direction: column !important; }
          .evcs-left  { width: 100% !important; min-height: 40vh; }
          .evcs-right { width: 100% !important; }
        }
      `}</style>

      {/* LEFT 38% — full-panel charger dot grid with cursor spotlight */}
      <div className="evcs-left" style={{
        width: "38%", position: "relative", overflow: "hidden", cursor: "crosshair",
        background: "linear-gradient(160deg, #071409 0%, #0D2010 40%, #163318 70%, #0A1C0C 100%)",
      }}
        onMouseMove={e => {
          const r = e.currentTarget.getBoundingClientRect();
          const x = e.clientX - r.left;
          const y = e.clientY - r.top;
          e.currentTarget.children[0].style.backgroundImage =
            `radial-gradient(220px circle at ${x}px ${y}px, rgba(74,222,128,0.28), transparent 70%),` +
            `radial-gradient(circle, rgba(74,222,128,0.6) 2.5px, transparent 2.5px)`;
          e.currentTarget.children[0].style.backgroundSize = `100% 100%, 20px 20px`;
        }}
        onMouseLeave={e => {
          e.currentTarget.children[0].style.backgroundImage =
            `radial-gradient(circle, rgba(74,222,128,0.38) 2.5px, transparent 2.5px)`;
          e.currentTarget.children[0].style.backgroundSize = `20px 20px`;
        }}
      >
        <div style={{
          position: "absolute", inset: 0,
          backgroundImage: "radial-gradient(circle, rgba(74,222,128,0.38) 2.5px, transparent 2.5px)",
          backgroundSize: "20px 20px",
        }} />
      </div>

      {/* RIGHT 62% — white sign-in panel */}
      <div className="evcs-right" style={{
        width: "62%", background: "#fff",
        display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
        padding: "60px 52px", gap: 0,
      }}>
        <div className="evcs-signin-in" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 28, width: "100%", maxWidth: 380 }}>

          <div style={{ width: "100%", height: 1, background: "#F3F4F6" }} />

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, width: "100%" }}>
            <div style={{
              display: "inline-flex", alignSelf: "center",
              background: "#F0FDF4", border: "1px solid #BBF7D0",
              color: "#166534", fontSize: 10, fontWeight: 700,
              borderRadius: 20, padding: "5px 14px", letterSpacing: "0.07em",
            }}>
              FLEET OPERATOR PORTAL
            </div>
            <div style={{ fontSize: 28, fontWeight: 800, color: "#111827", letterSpacing: "-0.02em", textAlign: "center" }}>
              Welcome back
            </div>
            <div style={{ fontSize: 13, color: "#6B7280", lineHeight: 1.6, textAlign: "center", maxWidth: 280 }}>
              Sign in to access the fleet intelligence dashboard.
            </div>
            <SignInButton mode="modal">
              <button style={{
                width: "100%", padding: "14px 0",
                borderRadius: 10, cursor: "pointer",
                background: "linear-gradient(135deg, #166534 0%, #15803D 100%)",
                border: "none", color: "#fff",
                fontSize: 15, fontWeight: 700,
                boxShadow: "0 4px 16px rgba(22,101,52,0.35)",
              }}>
                Sign in
              </button>
            </SignInButton>
          </div>

          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4 }}>
            <div style={{ fontSize: 11, color: "#9CA3AF" }}>Authorized personnel only</div>
            <div style={{ fontSize: 10, color: "#C4C9D4", fontFamily: "DM Mono, monospace" }}>Powered by Data Pigeon</div>
          </div>
        </div>
      </div>
    </div>
  );
}
